"use client";

import { useState, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import "@/styles/glassmorphism.css";
import { useLanguage } from "@/utils/LanguageContext";
import { useScrollFade } from "@/lib/MotionProvider";

const DEFAULT_HERO_VIDEO_ID = "rmtxuv1gCQQ";

const HERO_VIDEO_BY_LANG: Record<string, string> = {
  ar: "b4DK6sI_E2g",
  it: "H_3R8Espoqc",
  ko: "7Ibj6iRGNIo",
  ru: "W4GvyTKuRPA",
  ur: "QTR_SypNB68",
  zh: "v5W3mCMXs-0",
};

const Hero = () => {
  const { t, lang } = useLanguage();
  const [showHeroVideo, setShowHeroVideo] = useState(false);
  const heroVideoId = HERO_VIDEO_BY_LANG[lang] ?? DEFAULT_HERO_VIDEO_ID;

  const titleRef = useRef<HTMLHeadingElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);

  useScrollFade(titleRef);
  useScrollFade(descRef);

  const primerCards = [
    {
      key: "01",
      title: t("home_answer_what_title"),
      body: t("home_answer_what_body"),
    },
    {
      key: "02",
      title: t("home_answer_credible_title"),
      body: t("home_answer_credible_body"),
    },
    {
      key: "03",
      title: t("home_answer_token_title"),
      body: t("home_answer_token_body"),
    },
    {
      key: "04",
      title: t("home_answer_next_title"),
      body: t("home_answer_next_body"),
    },
  ];

  const proofItems = [
    {
      title: t("home_proof_item_1_title"),
      body: t("home_proof_item_1_body"),
    },
    {
      title: t("home_proof_item_2_title"),
      body: t("home_proof_item_2_body"),
    },
    {
      title: t("home_proof_item_3_title"),
      body: t("home_proof_item_3_body"),
    },
  ];

  return (
    <section
      className="hero-wrapper hero-1 home-anchor-target"
      id="home-hero"
      aria-labelledby="home-hero-title"
    >
      <div
        className="hero-bg-image"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: "-1",
        }}
      >
        <Image
          src="/assets/img/bg/Hero-3840x2160.webp"
          alt=""
          fill
          priority
          sizes="100vw"
          style={{ objectFit: "cover" }}
        />
      </div>

      <div
        className="hero-overlay"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "radial-gradient(circle, rgba(3, 26, 47, 0.7) 30%, rgba(3, 26, 47, 0.95) 100%)",
          zIndex: "-1",
        }}
      />

      <div className="ripple-shape">
        <span className="ripple-1"></span>
        <span className="ripple-2"></span>
        <span className="ripple-3"></span>
        <span className="ripple-4"></span>
        <span className="ripple-5"></span>
      </div>

      <div className="container mb-5">
        <div className="hero-style1 hero-shell">
          <div className="hero-top-grid">
            <div className="hero-copy-panel">
              <span className="hero-eyebrow">{t("home_hero_eyebrow")}</span>
              <h1 ref={titleRef} id="home-hero-title" className="hero-title">
                {t("home_hero_title")}
              </h1>
              <p ref={descRef} className="hero-titlepara">
                {t("home_hero_description")}
              </p>

              <div className="hero-actions-primary">
                <Link href={`/${lang}/flipbook/whitepaper`} className="hero-action-link">
                  {t("Read Whitepaper →")}
                </Link>
                <a
                  href="https://t.me/Invtrondaogroup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hero-action-link"
                >
                  {t("Join Telegram →")}
                </a>
              </div>
            </div>

            <aside className="hero-proof-panel" aria-label={t("home_proof_title")}>
              <div className="hero-proof-copy">
                <span className="hero-proof-kicker">INVTRON DAO</span>
                <h2 className="hero-proof-title">{t("home_proof_title")}</h2>
                <p className="hero-proof-description">{t("home_proof_description")}</p>
              </div>

              <div className="hero-proof-list">
                {proofItems.map((item) => (
                  <article key={item.title} className="hero-proof-item">
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                  </article>
                ))}
              </div>

              <div className="hero-proof-assets">
                <div className="hero-proof-badge hero-proof-badge--impact">
                  <Image
                    src="/impactwebsummi.png"
                    alt="Web Summit Impact logo"
                    width={144}
                    height={144}
                    sizes="(max-width: 768px) 96px, 144px"
                    className="hero-impact-logo"
                  />
                </div>

                <a
                  href="https://www.crunchbase.com/organization/invtron-dao"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hero-proof-badge hero-proof-badge--crunchbase"
                  aria-label="View Invtron on Crunchbase"
                >
                  <span className="hero-crunchbase-mark">cb</span>
                  <span className="hero-crunchbase-name">Crunchbase</span>
                </a>
              </div>

              <div className="hero-proof-links">
                <a
                  href="https://dashboard.invtron.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hero-proof-link"
                >
                  {t("Governance Dashboard →")}
                </a>
                <a
                  href="https://simulation.invtron.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hero-proof-link"
                >
                  {t("Tokenomics Simulation (ROI) →")}
                </a>
              </div>
            </aside>
          </div>

          <div className="hero-primer-grid" id="why-invtron">
            {primerCards.map((card) => (
              <article key={card.key} className="hero-primer-card">
                <span className="hero-primer-index">{card.key}</span>
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>

          <div className="row g-4 hero-main-row">
            <div className="col-12">
              <div className="hero-video-stack">
                <div
                  className="video-container"
                  style={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    position: "relative",
                    background:
                      "linear-gradient(180deg, rgba(2, 19, 35, 0.75), rgba(2, 19, 35, 0.92))",
                    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.28)",
                  }}
                >
                  {showHeroVideo ? (
                    <iframe
                      width="100%"
                      height="315"
                      src={`https://www.youtube-nocookie.com/embed/${heroVideoId}?rel=0&autoplay=1&modestbranding=1`}
                      title="INVTRON DAO Video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ display: "block", width: "100%", height: "100%" }}
                    ></iframe>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowHeroVideo(true)}
                      className="hero-video-poster"
                      aria-label={t("Play video")}
                    >
                      <span className="hero-video-poster-art" aria-hidden="true">
                        <Image
                          src="/assets/img/logo/logo.png"
                          alt=""
                          width={600}
                          height={600}
                          priority
                          sizes="(max-width: 991px) 60vw, 320px"
                          className="hero-video-logo"
                        />
                      </span>
                      <span className="hero-video-overlay" />
                      <span className="hero-video-content">
                        <span className="hero-video-play">
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M8 5.14v13.72L19 12 8 5.14Z" fill="currentColor" />
                          </svg>
                        </span>
                        <span className="hero-video-copy">
                          <strong>{t("Play video")}</strong>
                        </span>
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .hero-shell {
          display: flex;
          flex-direction: column;
          gap: 28px;
        }

        .hero-top-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.18fr) minmax(320px, 0.82fr);
          gap: 22px;
          align-items: start;
        }

        .hero-copy-panel,
        .hero-proof-panel,
        .hero-primer-card {
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(189, 213, 231, 0.16);
          background:
            linear-gradient(160deg, rgba(5, 20, 38, 0.76), rgba(7, 27, 49, 0.54)),
            radial-gradient(circle at top left, rgba(74, 148, 255, 0.16), transparent 48%);
          box-shadow:
            0 24px 60px rgba(2, 11, 24, 0.26),
            inset 0 1px 0 rgba(255, 255, 255, 0.06);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
        }

        .hero-copy-panel::before,
        .hero-proof-panel::before,
        .hero-primer-card::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(203, 226, 255, 0.06), transparent 34%);
          pointer-events: none;
        }

        .hero-copy-panel {
          padding: clamp(26px, 4vw, 40px);
          border-radius: 28px;
        }

        .hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-height: 34px;
          padding: 6px 14px;
          border-radius: 999px;
          border: 1px solid rgba(186, 221, 255, 0.2);
          background: rgba(255, 255, 255, 0.06);
          color: #d4e7fb;
          font-size: 0.78rem;
          font-weight: 800;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .hero-title {
          max-width: 12ch;
          margin: 18px 0 0;
          color: #f1f7ff;
          font-family: "Lexend Deca", var(--tg-heading-font-family);
          font-size: clamp(2.8rem, 2.3rem + 1.7vw, 4.55rem);
          line-height: 0.98;
          letter-spacing: -0.055em;
        }

        .hero-titlepara {
          max-width: 62ch;
          margin: 18px 0 0;
          color: rgba(226, 238, 249, 0.84);
          font-size: clamp(1.04rem, 0.96rem + 0.25vw, 1.18rem);
          line-height: 1.78;
        }

        .hero-actions-primary,
        .hero-actions-secondary,
        .hero-proof-assets,
        .hero-proof-links {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .hero-actions-primary {
          margin-top: 28px;
        }

        .hero-actions-secondary {
          margin-top: 12px;
        }

        .hero-primary-cta {
          min-width: min(240px, 100%);
          min-height: 58px;
          padding-inline: 28px;
          font-size: 0.98rem;
          font-weight: 800;
          justify-content: center;
        }

        :global(.hero-action-link),
        .hero-secondary-link,
        .hero-utility-link,
        .hero-proof-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 1 auto;
          min-height: 50px;
          min-width: min(240px, 100%);
          padding: 12px 18px;
          border-radius: 16px;
          border: 1px solid rgba(189, 213, 231, 0.14);
          background: rgba(255, 255, 255, 0.05);
          color: #e8f2fd;
          font-weight: 700;
          text-decoration: none;
          transition:
            transform 0.2s ease,
            background 0.2s ease,
            border-color 0.2s ease;
        }

        :global(.hero-action-link:hover),
        .hero-secondary-link:hover,
        .hero-utility-link:hover,
        .hero-proof-link:hover {
          transform: translateY(-2px);
          border-color: rgba(132, 198, 255, 0.32);
          background: rgba(255, 255, 255, 0.09);
        }

        :global(.hero-action-link:focus-visible),
        .hero-secondary-link:focus-visible,
        .hero-utility-link:focus-visible,
        .hero-proof-link:focus-visible {
          outline: 2px solid #7ab6ff;
          outline-offset: 4px;
        }

        .hero-proof-panel {
          display: flex;
          flex-direction: column;
          gap: 18px;
          padding: clamp(22px, 3vw, 28px);
          border-radius: 26px;
        }

        .hero-proof-kicker {
          color: rgba(211, 232, 251, 0.72);
          font-size: 0.74rem;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .hero-proof-title {
          margin: 10px 0 0;
          color: #f1f7ff;
          font-size: clamp(1.5rem, 1.3rem + 0.55vw, 2rem);
          line-height: 1.1;
          letter-spacing: -0.04em;
        }

        .hero-proof-description {
          margin: 12px 0 0;
          color: rgba(226, 238, 249, 0.82);
          line-height: 1.72;
        }

        .hero-proof-list {
          display: grid;
          gap: 12px;
        }

        .hero-proof-item {
          padding: 14px 16px;
          border-radius: 18px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          background:
            linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(3, 18, 34, 0.2)),
            rgba(255, 255, 255, 0.03);
        }

        .hero-proof-item h3 {
          margin: 0;
          color: #eff6ff;
          font-size: 1rem;
          font-weight: 700;
        }

        .hero-proof-item p {
          margin: 8px 0 0;
          color: rgba(222, 236, 248, 0.78);
          line-height: 1.65;
        }

        .hero-proof-assets {
          margin-top: auto;
        }

        .hero-proof-badge {
          min-height: 112px;
          border-radius: 22px;
          border: 1px solid rgba(189, 213, 231, 0.14);
          background:
            radial-gradient(circle at top left, rgba(69, 147, 255, 0.14), transparent 48%),
            rgba(255, 255, 255, 0.05);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
        }

        .hero-proof-badge--impact {
          flex: 1 1 142px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 12px;
        }

        .hero-impact-logo {
          width: 100%;
          max-width: 118px;
          height: auto;
        }

        .hero-proof-badge--crunchbase {
          flex: 1 1 142px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 18px;
          color: #eff6ff;
          text-decoration: none;
          transition: transform 0.2s ease, border-color 0.2s ease;
        }

        .hero-proof-badge--crunchbase:hover {
          transform: translateY(-2px);
          border-color: rgba(132, 198, 255, 0.3);
        }

        .hero-crunchbase-mark {
          min-width: 52px;
          height: 52px;
          padding: 0 12px;
          border-radius: 16px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #0a65ff;
          color: #fff;
          font-family: "Lexend Deca", var(--tg-heading-font-family);
          font-size: 1.4rem;
          font-weight: 700;
          line-height: 1;
          letter-spacing: -0.04em;
          text-transform: lowercase;
        }

        .hero-crunchbase-name {
          font-size: 1rem;
          font-weight: 700;
          line-height: 1.2;
        }

        .hero-proof-links {
          gap: 10px;
        }

        .hero-proof-link {
          flex: 1 1 180px;
          min-height: 46px;
          font-size: 0.95rem;
        }

        .hero-primer-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px;
        }

        .hero-primer-card {
          min-height: 100%;
          padding: 22px 20px;
          border-radius: 22px;
        }

        .hero-primer-index {
          display: inline-flex;
          min-height: 28px;
          align-items: center;
          justify-content: center;
          padding: 3px 10px;
          border-radius: 999px;
          border: 1px solid rgba(186, 221, 255, 0.18);
          background: rgba(255, 255, 255, 0.06);
          color: rgba(212, 231, 251, 0.8);
          font-size: 0.75rem;
          font-weight: 800;
          letter-spacing: 0.1em;
        }

        .hero-primer-card h3 {
          margin: 16px 0 0;
          color: #eff6ff;
          font-size: 1.15rem;
          font-weight: 700;
          line-height: 1.25;
        }

        .hero-primer-card p {
          margin: 12px 0 0;
          color: rgba(222, 236, 248, 0.8);
          line-height: 1.7;
        }

        .hero-main-row {
          align-items: stretch;
        }

        .hero-main-row > [class*="col-"] {
          display: flex;
        }

        .hero-video-stack,
        .hero-sale-stack {
          flex: 1 1 auto;
          width: 100%;
          max-width: 620px;
          height: 100%;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
        }

        .hero-sale-stack :global(.ts-modal-content) {
          height: 100%;
          max-height: none;
          min-height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          overflow: visible;
        }

        .hero-sale-stack :global(.ts-round-card) {
          flex: 1 1 auto;
          min-height: 345px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .video-container {
          border: 1px solid rgba(189, 213, 231, 0.12);
          display: flex;
          flex: 1 1 auto;
          min-height: 345px;
        }

        .hero-video-poster {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          min-height: 345px;
          padding: 0;
          border: 0;
          background:
            radial-gradient(circle at 50% 28%, rgba(72, 147, 255, 0.26), transparent 34%),
            linear-gradient(180deg, rgba(4, 21, 38, 0.9), rgba(2, 14, 26, 0.98));
          cursor: pointer;
          text-align: left;
        }

        .hero-video-poster-art {
          position: relative;
          z-index: 0;
          width: min(52%, 320px);
          min-width: 220px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
        }

        .hero-video-logo {
          width: 100%;
          height: auto;
          filter: drop-shadow(0 20px 36px rgba(1, 7, 15, 0.36));
        }

        .video-container iframe {
          display: block;
          width: 100%;
          height: 100%;
          min-height: 345px;
        }

        .hero-video-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            180deg,
            rgba(3, 26, 47, 0.08),
            rgba(3, 26, 47, 0.72)
          );
        }

        .hero-video-content {
          position: absolute;
          inset: auto 18px 18px 18px;
          display: flex;
          align-items: center;
          gap: 14px;
          color: #fff;
          z-index: 1;
        }

        .hero-video-play {
          width: 60px;
          height: 60px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          border: 1px solid rgba(255, 255, 255, 0.4);
          background: rgba(255, 255, 255, 0.18);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          flex-shrink: 0;
        }

        .hero-video-play svg {
          width: 28px;
          height: 28px;
          margin-left: 2px;
        }

        .hero-video-copy {
          display: flex;
          flex-direction: column;
        }

        .hero-video-copy strong {
          font-size: 1rem;
          font-weight: 700;
        }

        @media (max-width: 991px) {
          .hero-style1 {
            padding-top: clamp(118px, 24vw, 146px);
          }

          .hero-top-grid {
            grid-template-columns: 1fr;
          }

          .hero-primer-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .hero-title {
            max-width: none;
          }
        }

        @media (max-width: 768px) {
          .hero-shell {
            gap: 20px;
          }

          .hero-copy-panel,
          .hero-proof-panel,
          .hero-primer-card {
            border-radius: 22px;
          }

          .hero-title {
            font-size: clamp(2.25rem, 1.95rem + 1.5vw, 3rem);
          }

          .hero-actions-primary {
            flex-direction: column;
          }

          .hero-primary-cta,
          :global(.hero-action-link),
          .hero-secondary-link,
          .hero-utility-link,
          .hero-proof-link {
            width: 100%;
          }

          .hero-primer-grid {
            grid-template-columns: 1fr;
          }

          .hero-main-row > [class*="col-"] {
            display: block;
          }

          .hero-sale-stack :global(.ts-round-card) {
            min-height: 0;
          }

          .hero-video-poster,
          .video-container,
          .video-container iframe {
            min-height: 250px;
          }

          .hero-video-poster-art {
            width: min(62%, 260px);
            min-width: 180px;
          }

          .hero-video-content {
            inset: auto 14px 14px 14px;
            gap: 12px;
          }

          .hero-video-play {
            width: 52px;
            height: 52px;
          }

          .hero-video-play svg {
            width: 24px;
            height: 24px;
          }

          .hero-video-copy strong {
            font-size: 0.95rem;
          }
        }

        @media (max-width: 420px) {
          .hero-copy-panel,
          .hero-proof-panel,
          .hero-primer-card {
            padding-inline: 18px;
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;
